import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CompanySelector.css';

const CompanySelector = ({ token, onCompanySelected }) => {
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    // Mapping of company names to logo files
    const companyLogos = {
        'STL-Digital': '/Main.png',
        'ONB': '/STL_Logo.png',
        'Invenia': '/Invenia.png',
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    const fetchCompanies = async () => {
        try {
            setLoading(true);
            setError(''); // Clear any previous errors
            // Clear error flag from localStorage when starting fresh fetch
            localStorage.removeItem('company_selector_error');

            // Use the same API URL pattern as other components
            const apiUrl = process.env.REACT_APP_API_URL || 'https://django-backend-115705416374.asia-south1.run.app';
            const response = await fetch(`${apiUrl}/api/vendor-companies/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch companies');
            }

            const data = await response.json();
            const companiesList = data.companies || [];
            setCompanies(companiesList);

            console.log('✅ Fetched companies:', companiesList);

            // Auto-select if only one company
            if (companiesList.length === 1) {
                setSelectedCompanyId(companiesList[0].id);
                console.log('✅ Auto-selected single company:', companiesList[0].name);
            } else if (companiesList.length === 0) {
                setError('No companies found for your account. Please contact support.');
            }

            setLoading(false);
        } catch (err) {
            console.error('Error fetching companies:', err);
            setError('Failed to load companies. Please try again.');
            setLoading(false);
            // Set error flag in localStorage to prevent redirect on refresh
            localStorage.setItem('company_selector_error', 'true');
        }
    };

    const handleConfirm = async () => {
        if (!selectedCompanyId) {
            alert('Please select a company');
            return;
        }

        try {
            setLoading(true);
            setError(''); // Clear any previous errors
            // Clear error flag when successfully setting company
            localStorage.removeItem('company_selector_error');

            // Use the same API URL pattern as other components
            const apiUrl = process.env.REACT_APP_API_URL || 'https://django-backend-115705416374.asia-south1.run.app';
            const response = await fetch(`${apiUrl}/api/set-active-company/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ company_id: selectedCompanyId }),
            });

            if (!response.ok) {
                throw new Error('Failed to set active company');
            }

            const data = await response.json();
            console.log('✅ Company selected:', data.company?.name || data.company?.name);

            // ✅ Set session flag to prevent double-prompting on dashboard
            sessionStorage.setItem('vendor_company_selected', 'true');

            // Call callback or navigate
            if (onCompanySelected) {
                onCompanySelected(data.company);
            } else {
                // Navigate to root - dashboard will load with selected company
                window.location.href = '/';
            }
        } catch (err) {
            console.error('Error setting company:', err);
            setError('Failed to set company. Please try again.');
            setLoading(false);
            // Set error flag in localStorage to prevent redirect on refresh
            localStorage.setItem('company_selector_error', 'true');
        }
    };

    if (loading && companies.length === 0) {
        return (
            <div className="cs-container">
                <div className="cs-box">
                    <div className="cs-loading-spinner">
                        <div className="spinner"></div>
                        <p>Loading your companies...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="cs-container">
                <div className="cs-box">
                    <div className="cs-error-message">
                        <h2>⚠️ Error</h2>
                        <p>{error}</p>
                        <button onClick={fetchCompanies} className="cs-retry-button">
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="cs-container">
            <div className="cs-box">
                {/* Header */}
                <div className="cs-header">
                    <div className="cs-header-icon">🏢</div>
                    <h1>Select Your Company</h1>
                    <p>Choose which company you want to access</p>
                </div>

                {/* Company Cards */}
                {companies.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <p style={{ color: '#718096', fontSize: '16px' }}>No companies available. Please contact support.</p>
                    </div>
                ) : (
                    <div className="cs-grid">
                        {companies.map((company) => {
                            // Create a safe class name from company name (e.g., 'STL-Digital' -> 'cs-company-stl-digital')
                            const companyClassName = `cs-company-${company.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;

                            return (
                                <div
                                    key={company.id}
                                    className={`cs-card ${companyClassName} ${selectedCompanyId === company.id ? 'cs-selected' : ''}`}
                                    onClick={() => setSelectedCompanyId(company.id)}
                                >
                                    <div className="cs-card-inner">
                                        {/* Selection Indicator */}
                                        <div className="cs-selection-indicator">
                                            {selectedCompanyId === company.id && (
                                                <div className="cs-checkmark">✓</div>
                                            )}
                                        </div>

                                        {/* Company Logo */}
                                        <div className="cs-logo-container">
                                            <img draggable={false}
                                                src={process.env.PUBLIC_URL + (companyLogos[company.name] || '/Main.png')}
                                                alt={`${company.name} Logo`}
                                                className="cs-logo"
                                                onError={(e) => {
                                                    e.target.src = process.env.PUBLIC_URL + '/Main.png';
                                                }}
                                            />
                                        </div>

                                        {/* Company Name */}
                                        <div className="cs-name">
                                            <h3>{company.name}</h3>
                                        </div>

                                        {/* Radio Circle */}
                                        <div className="cs-radio-circle">
                                            <div className={`cs-radio-inner ${selectedCompanyId === company.id ? 'cs-checked' : ''}`}></div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Confirm Button */}
                <div className="cs-confirm-section">
                    <button
                        onClick={handleConfirm}
                        disabled={!selectedCompanyId || loading}
                        className="cs-confirm-button"
                    >
                        {loading ? (
                            <>
                                <span className="cs-button-spinner"></span>
                                Processing...
                            </>
                        ) : (
                            <>
                                <span className="button-icon">→</span>
                                Confirm Selection
                            </>
                        )}
                    </button>
                </div>

                {/* Helper Text */}
                <div className="cs-helper-text">
                    <p>✨ You can switch companies anytime from your dashboard</p>
                </div>
            </div>
        </div>
    );
};



export default CompanySelector;
